/**
 * The bench (PRD 10.7, Design Review V-16; Phase 29): unallocated hours by role by week, read
 * against the pipeline that could fill them. It is the inverse of the demand ribbon: where the
 * ribbon stacks what is asked of each week and draws the capacity line over it, the bench stacks
 * what is left of each week and draws the pipeline line over that.
 *
 * Three rules live here, shared by the read model behind `GET /forecast/bench`, the card and the
 * tests, so none of them can hold a different opinion of what the bench is:
 *
 * - **Bench is per role, floored at zero.** A role's bench in a week is its capacity less its
 *   committed demand (every layer of the ribbon except the pipeline), and never below zero: an
 *   overloaded role has no bench, and its overload does not cancel another role's bench. That
 *   is why the company's bench in a week is at least its headroom, and usually more.
 * - **The pipeline is the line, not part of the bar.** The bench is what the pipeline could
 *   fill, so the pipeline is not subtracted from it; it is drawn against it, in the mode the
 *   reader chose, and the gap between the two is the sales target.
 * - **A gap is matched by role.** A design deal cannot fill a development bench. The gap that
 *   is drawn is the week's total (bench above the line); the gap that is called out is by
 *   role, and the role gaps of a week sum to at least the drawn one.
 *
 * Pure: no DOM, no I/O.
 */
import { MAX_TYPE_HUES, OTHER_SLOT, assignTypePalette } from './palette.js';
import { col } from './table.js';

/** The shared filters this view reads, in the API's names. */
export const BENCH_PARAM_KEYS = Object.freeze([
  'run_id',
  'from',
  'weeks',
  'dimension',
  'entity',
  'pipeline',
]);

/** The bench has no project dimension: a project's leftover against company capacity is meaningless. */
export const BENCH_DIMENSIONS = Object.freeze([
  'company',
  'department',
  'service',
  'office',
  'individual',
]);

/** How many roles take a hue of their own before the rest fold into one neutral segment. */
export const MAX_ROLE_HUES = MAX_TYPE_HUES;

/** The folded segment: every role past the hued ones, drawn once in the neutral slot. */
export const OTHER_ROLES = Object.freeze({
  key: 'other_roles',
  label: 'Other roles',
  token: OTHER_SLOT.var,
});

/** How many gap callouts the card shows before the rest go to the table. */
export const CALLOUTS_SHOWN = 5;

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const round4 = (n) => Math.round((n + Number.EPSILON) * 10000) / 10000;
const num = (v) => (v === null || v === undefined || v === '' ? 0 : Number(v) || 0);

/**
 * A role's bench in a week: capacity less committed demand, floored at zero.
 * @param {number} capacityHours
 * @param {number} committedHours  every layer but the pipeline
 */
export function benchHours(capacityHours, committedHours) {
  return round2(Math.max(0, num(capacityHours) - num(committedHours)));
}

/** The bench the pipeline does not reach: bench less pipeline, floored at zero. */
export function gapHours(benchHours_, pipelineHours) {
  return round2(Math.max(0, num(benchHours_) - num(pipelineHours)));
}

/** Pipeline over bench, floored at zero: deals that would land on a week with nothing left. */
export function surplusHours(benchHours_, pipelineHours) {
  return round2(Math.max(0, num(pipelineHours) - num(benchHours_)));
}

/** How much of the bench the pipeline covers; null where there is no bench to cover. */
export function coverage(benchHours_, pipelineHours) {
  const b = num(benchHours_);
  if (b <= 0) return null;
  return round4(num(pipelineHours) / b);
}

/** What the line is called once the read mode is known. */
export function pipelineLineLabel(pipelineMode) {
  if (pipelineMode === 'none') return 'Pipeline (excluded)';
  return pipelineMode === 'unweighted' ? 'Unweighted pipeline' : 'Weighted pipeline';
}

/**
 * Roles in the order the bench ranks them: the most bench over the window first, ties by label
 * then key, so two roles with the same figure never swap hues between runs. Adds `rank` (1-based).
 * @param {Array<{ key: string, label?: string, bench_hours: number }>} roles
 */
export function rankRoles(roles) {
  return [...(roles ?? [])]
    .map((r) => ({ ...r, label: r.label ?? r.key }))
    .sort(
      (a, b) =>
        num(b.bench_hours) - num(a.bench_hours) ||
        a.label.localeCompare(b.label) ||
        a.key.localeCompare(b.key),
    )
    .map((r, i) => ({ ...r, rank: i + 1 }));
}

/**
 * The hue each role wears: the first `MAX_ROLE_HUES` by bench take the type slots in slot
 * order, the rest read as Other. It is the palette's own assignment (`assignTypePalette`) with
 * bench hours as the volume, so the adjacent-pair validation of the slot order carries over —
 * the stack is drawn in rank order, and rank order is slot order.
 *
 * The input is the whole response's roles, never a filtered subset, so a role keeps its hue
 * while the reader hides others.
 * @param {Array<{ key: string, label?: string, bench_hours: number }>} roles
 * @returns {Map<string, { slot: number, hue: string, token: string, rank: number }>}
 */
export function roleSlots(roles) {
  const ranked = rankRoles(roles);
  const assigned = assignTypePalette(
    ranked.map((r) => ({ value: r.key, label: r.label, volume: num(r.bench_hours), priority: 1 })),
  );
  const out = new Map();
  for (const a of assigned) {
    const rank = ranked.find((r) => r.key === a.value)?.rank ?? ranked.length + 1;
    out.set(a.value, { slot: a.slot, hue: a.hue, token: a.var, rank });
  }
  return out;
}

/**
 * The segments the stack is drawn in, bottom-up: the hued roles in rank order, then one folded
 * segment for the rest (absent when every role has a hue of its own).
 * @param {Array<{ key: string, label?: string, bench_hours: number }>} roles
 * @returns {{ drawn: Array<{ key, label, token, rank, bench_hours }>, other: { key, label, token, roles: Array } | null }}
 */
export function foldRoles(roles) {
  const slots = roleSlots(roles);
  const ranked = rankRoles(roles);
  const drawn = [];
  const folded = [];
  for (const r of ranked) {
    const s = slots.get(r.key);
    if (s && s.slot <= MAX_ROLE_HUES) drawn.push({ ...r, token: s.token });
    else folded.push(r);
  }
  return {
    drawn,
    other: folded.length ? { ...OTHER_ROLES, roles: folded } : null,
  };
}

/**
 * One week's stack: `[from, to]` per segment in drawing order, the folded roles summed into
 * their one segment. The segments sum to the week's bench by construction.
 * @param {Record<string, { bench_hours: number }>} weekRoles  `weeks[].roles` of the response
 * @param {ReturnType<typeof foldRoles>} fold
 */
export function benchStack(weekRoles, fold) {
  let base = 0;
  const segment = (key, label, token, hours, extra = {}) => {
    const s = { key, label, token, from: base, to: round2(base + hours), hours, ...extra };
    base = s.to;
    return s;
  };
  const segments = fold.drawn.map((r) =>
    segment(r.key, r.label, r.token, round2(num(weekRoles?.[r.key]?.bench_hours))),
  );
  if (fold.other) {
    const parts = fold.other.roles
      .map((r) => ({
        key: r.key,
        label: r.label,
        hours: round2(num(weekRoles?.[r.key]?.bench_hours)),
      }))
      .filter((p) => p.hours > 0);
    segments.push(
      segment(
        fold.other.key,
        fold.other.label,
        fold.other.token,
        round2(parts.reduce((s, p) => s + p.hours, 0)),
        { roles: parts },
      ),
    );
  }
  return segments;
}

/** The top of the value axis: the largest bench or pipeline figure in the window. */
export function benchScaleMax(weeks) {
  let max = 0;
  for (const w of weeks ?? []) max = Math.max(max, num(w.bench_hours), num(w.pipeline_hours));
  return max;
}

/**
 * The gap callouts (PRD 10.1 "210 h Design/Content from Nov 30, no pipeline against"; PRD 10.7
 * "Wk49 needs 30 h more Design/Content pipeline"): for each role, every run of consecutive weeks
 * in which its bench exceeds its pipeline, with the hours summed over the run. Largest gap first,
 * then the earlier start, then the label.
 *
 * @param {{ weeks: Array<{ week_start: string, week_end: string, roles: Record<string, { bench_hours, pipeline_hours, gap_hours }> }>,
 *           roles: Array<{ key: string, label?: string }> }} data
 */
export function benchCallouts(data) {
  const out = [];
  for (const role of data?.roles ?? []) {
    let run = null;
    const close = () => {
      if (run) out.push(run);
      run = null;
    };
    for (const w of data.weeks ?? []) {
      const cell = w.roles?.[role.key];
      const gap = round2(num(cell?.gap_hours));
      if (gap <= 0) {
        close();
        continue;
      }
      if (!run) {
        run = {
          role: role.key,
          label: role.label ?? role.key,
          from: w.week_start,
          to: w.week_end,
          weeks: 0,
          bench_hours: 0,
          pipeline_hours: 0,
          gap_hours: 0,
        };
      }
      run.to = w.week_end;
      run.weeks += 1;
      run.bench_hours = round2(run.bench_hours + num(cell.bench_hours));
      run.pipeline_hours = round2(run.pipeline_hours + num(cell.pipeline_hours));
      run.gap_hours = round2(run.gap_hours + gap);
    }
    close();
  }
  return out.sort(
    (a, b) =>
      b.gap_hours - a.gap_hours || a.from.localeCompare(b.from) || a.label.localeCompare(b.label),
  );
}

/**
 * The sentence a callout reads as, in the PRD's words: the role, the shortfall, when, and what
 * stands against the bench.
 * @param {ReturnType<typeof benchCallouts>[number]} c
 * @param {{ hours?: (h: number) => string, week?: (iso: string) => string }} [format]
 */
export function calloutText(c, { hours = (h) => `${h} h`, week = (iso) => iso } = {}) {
  const when = c.weeks > 1 ? `${c.weeks} weeks from ${week(c.from)}` : `week of ${week(c.from)}`;
  const against =
    c.pipeline_hours > 0
      ? `${hours(c.pipeline_hours)} of pipeline against ${hours(c.bench_hours)} on the bench`
      : `${hours(c.bench_hours)} on the bench, no pipeline against it`;
  return `${c.label}: needs ${hours(c.gap_hours)} more pipeline, ${when} (${against})`;
}

/**
 * The headline figures of a response, for the card's KPIs and the tests.
 * @param {object} data  a `GET /forecast/bench` response
 */
export function benchSummary(data) {
  const t = data?.totals ?? {};
  const weeks = data?.weeks ?? [];
  return {
    bench_hours: round2(num(t.bench_hours)),
    pipeline_hours: round2(num(t.pipeline_hours)),
    gap_hours: round2(num(t.gap_hours)),
    role_gap_hours: round2(num(t.role_gap_hours)),
    surplus_hours: round2(num(t.surplus_hours)),
    coverage: t.coverage ?? null,
    weeks: weeks.length,
    weeks_with_bench: Number(t.weeks_with_bench ?? 0),
    weeks_with_gap: Number(t.weeks_with_gap ?? 0),
    roles_with_bench: Number(t.roles_with_bench ?? 0),
    peak: t.peak ?? null,
    largest_gap: t.largest_gap ?? null,
  };
}

/**
 * One row per week for the table and the CSV: the drawn segments as columns, then the totals.
 * @param {object} data  a `GET /forecast/bench` response
 * @param {ReturnType<typeof foldRoles>} fold
 */
export function benchExportRows(data, fold) {
  return (data?.weeks ?? []).map((w) => {
    const stack = benchStack(w.roles, fold);
    return {
      week_start: w.week_start,
      week_end: w.week_end,
      ...Object.fromEntries(stack.map((s) => [`role_${s.key}`, s.hours])),
      bench_hours: round2(num(w.bench_hours)),
      pipeline_hours: round2(num(w.pipeline_hours)),
      gap_hours: round2(num(w.gap_hours)),
      role_gap_hours: round2(num(w.role_gap_hours)),
      surplus_hours: round2(num(w.surplus_hours)),
      coverage: w.coverage ?? null,
      capacity_hours: round2(num(w.capacity_hours)),
      committed_hours: round2(num(w.committed_hours)),
      headroom_hours: round2(num(w.headroom_hours)),
      roles_with_bench: Number(w.roles_with_bench ?? 0),
      people: Number(w.people ?? 0),
    };
  });
}

/** One row per role for the table and the CSV, in rank order. */
export function roleExportRows(data) {
  return rankRoles(data?.roles ?? []).map((r) => ({
    key: r.key,
    label: r.label,
    rank: r.rank,
    capacity_hours: round2(num(r.capacity_hours)),
    committed_hours: round2(num(r.committed_hours)),
    bench_hours: round2(num(r.bench_hours)),
    pipeline_hours: round2(num(r.pipeline_hours)),
    gap_hours: round2(num(r.gap_hours)),
    over_hours: round2(num(r.over_hours)),
    coverage: r.coverage ?? null,
    weeks_with_bench: Number(r.weeks_with_bench ?? 0),
    weeks_with_gap: Number(r.weeks_with_gap ?? 0),
    people: Number(r.people ?? 0),
  }));
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// Specifications, not renderings: the screen decorates them with `withFormats` and the CSV
// writer reads the same list, so an export cannot hold a different set of columns from the
// table it came from. The role columns and the pipeline line's label follow the data, so both
// take the model.

/**
 * The segments of the stack in drawing order, folded segment last — what both the chart's bands
 * and the week table's role columns are. Shared with the server's CSV (Phase 37).
 * @param {{ drawn: object[], other?: object|null }} fold  from `foldRoles`
 */
export function benchSegments(fold) {
  return [...(fold?.drawn ?? []), ...(fold?.other ? [fold.other] : [])];
}

/**
 * One row per week. `segments` are the role bands of the stack, in the order they are drawn.
 * @param {{ segments?: {key: string, label: string}[], pipelineMode?: string }} opts
 */
export function benchWeekColumns({ segments = [], pipelineMode = 'weighted' } = {}) {
  return [
    col.week('week_start', 'Week'),
    ...segments.map((seg) => col.hours(`role_${seg.key}`, seg.label)),
    col.hours('bench_hours', 'On the bench'),
    col.hours('pipeline_hours', pipelineLineLabel(pipelineMode)),
    col.hours('gap_hours', 'Gap'),
    col.hours('role_gap_hours', 'Gap by role'),
    col.hours('surplus_hours', 'Pipeline over bench'),
    col.percent('coverage', 'Covered'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('committed_hours', 'Committed'),
    col.hours('headroom_hours', 'Headroom'),
    col.count('roles_with_bench', 'Roles with bench'),
    col.count('people', 'People'),
  ];
}

/** One row per role, over the window. */
export function benchRoleColumns({ pipelineMode = 'weighted' } = {}) {
  return [
    col.count('rank', '#'),
    col.text('label', 'Role'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('committed_hours', 'Committed'),
    col.hours('bench_hours', 'On the bench'),
    col.hours('pipeline_hours', pipelineLineLabel(pipelineMode)),
    col.hours('gap_hours', 'Gap'),
    col.hours('over_hours', 'Over capacity'),
    col.percent('coverage', 'Covered'),
    col.count('weeks_with_bench', 'Weeks with bench'),
    col.count('weeks_with_gap', 'Weeks with gap'),
    col.count('people', 'People'),
  ];
}

/** One row per run of weeks a role is on the bench with pipeline to cover it. */
export function benchCalloutColumns({ pipelineMode = 'weighted' } = {}) {
  return [
    col.text('label', 'Role'),
    col.week('from', 'From'),
    col.week('to', 'To'),
    col.count('weeks', 'Weeks'),
    col.hours('bench_hours', 'On the bench'),
    col.hours('pipeline_hours', pipelineLineLabel(pipelineMode)),
    col.hours('gap_hours', 'Gap'),
  ];
}
